'use client';

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { collection, query, where, orderBy, limit, getDocs, documentId, startAfter, Timestamp, QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import Image from 'next/image';
import { Tweet } from '@/types/twitter';
import { Tag } from '@/types/tags';
import { tagService } from '@/lib/tag-service';
import { chunkArray, FIRESTORE_IN_QUERY_LIMIT } from '@/lib/batch-fetcher';
import { mergeChunkedTweets } from '@/lib/social-feed-merge';
import { CompactTweetCard } from './cards/CompactTweetCard';
import { ChevronDown, Loader2, MessageCircle, Repeat2, Heart } from 'lucide-react';
import { StateMark } from '@/components/ui/HorizonMark';

// Pagination constants - tune these for cost/UX balance
const INITIAL_FETCH_SIZE = 20;  // First load - fast initial paint
const LOAD_MORE_SIZE = 15;      // Subsequent loads - smaller batches = cheaper
const MAX_TOTAL_TWEETS = 200;   // Safety cap to prevent runaway costs

export interface TwitterFeedProps {
    tagSlug?: string;
    handles?: string[];
    selectedEntityId?: string;  // Filter by entity tag ID
    maxTweets?: number;         // Soft limit for display (pagination will go beyond if user requests)
    className?: string;
    variant?: 'compact' | 'timeline';
    hideEntityTags?: boolean;
    title?: React.ReactNode;
    showControls?: boolean;
    showRepliesDefault?: boolean;
    sortOption?: 'latest' | 'engaging';
    onSortChange?: (sort: 'latest' | 'engaging') => void;
    /** Focus-in-place: when set, an in-feed entity click calls back into the host page
        (e.g. to focus the chain dashboard's entity lens) instead of navigating away. */
    onEntitySelect?: (entityId: string, entitySlug: string) => void;
    /** Base path for entity social links/nav. The chain dashboard passes its scoped
        `/<chain>/social` so cards stay in the chain lens; the default targets the primary
        chain's social surface (the bare `/social` index is retired).
        TODO(multichain): derive the default from the active chain when chain #2 lands. */
    entitySocialBase?: string;
    // Handle filter dropdown props
    availableHandles?: string[];  // All handles for dropdown (when > 1, show dropdown)
    selectedHandle?: string;      // Currently selected handle ('all' or specific handle)
    onHandleChange?: (handle: string) => void;  // Callback when handle changes
}

type SortOption = 'latest' | 'engaging';

export function TwitterFeed({
    tagSlug,
    handles,
    selectedEntityId,
    maxTweets = 50,
    className = '',
    variant = 'timeline',
    hideEntityTags = false,
    title,
    showControls = true,
    sortOption: externalSortOption,
    onSortChange,
    availableHandles,
    selectedHandle: externalSelectedHandle,
    onHandleChange,
    onEntitySelect,
    entitySocialBase = '/abstract/social'
}: TwitterFeedProps) {
    const router = useRouter();
    
    // Core state
    const [tweets, setTweets] = useState<Tweet[]>([]);
    const [profiles, setProfiles] = useState<Record<string, string>>({});
    const [handleTagMap, setHandleTagMap] = useState<Map<string, Tag[]>>(new Map());
    
    // Loading states
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState('');
    const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
    
    // Pagination state - cursor-based for infinite scaling
    const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
    // Parallel VALUE cursor for the scatter-gather (>30 handles) path. A docSnapshot
    // cursor is only valid for the single chunk it came from, so the chunked path tracks
    // the orderField value of the last displayed tweet instead. See social-feed-merge.ts.
    const [boundaryValue, setBoundaryValue] = useState<number | string | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const [totalFetched, setTotalFetched] = useState(0);
    
    // Filters & Sorting
    const [internalSortOption, setInternalSortOption] = useState<SortOption>('latest');
    const sortOption = externalSortOption || internalSortOption;
    const [selectedHandle, setSelectedHandle] = useState<string>('all');
    const [allHandles, setAllHandles] = useState<string[]>([]);
    
    // Track current query params to detect changes
    const queryParamsRef = useRef<string>('');

    const handleSortChange = (newSort: SortOption) => {
        if (onSortChange) {
            onSortChange(newSort);
        } else {
            setInternalSortOption(newSort);
        }
    };

    // Detect widget mode (small maxTweets = no pagination needed)
    const isWidgetMode = maxTweets <= 10;

    // Scatter-gather applies ONLY when the active filter is the explicit-handles list
    // (filter #4 below) AND it exceeds Firestore's 30-value `in` cap. In every other
    // case (specific handle / entity / tag / <=30 handles) the single-query path runs
    // byte-identically to before.
    const useChunkedFetch =
        selectedHandle === 'all' &&
        !selectedEntityId &&
        !tagSlug &&
        !!handles &&
        handles.length > FIRESTORE_IN_QUERY_LIMIT;
    
    // Build query based on current filters
    const buildQuery = useCallback((isLoadMore: boolean = false) => {
        if (!db) return null;
        
        const tweetsRef = collection(db, 'tweets');
        const orderField = sortOption === 'engaging' ? 'trendingScore' : 'createdAt';
        // For widgets, fetch exactly what's needed. For full feeds, use pagination sizes.
        const fetchSize = isWidgetMode 
            ? maxTweets 
            : (isLoadMore ? LOAD_MORE_SIZE : INITIAL_FETCH_SIZE);
        
        let constraints: any[] = [];
        
        // Always filter out hidden tweets from public feed
        constraints.push(where('isHidden', '==', false));
        
        // 1. Specific Handle Filter
        if (selectedHandle !== 'all') {
            constraints.push(where('handleId', '==', selectedHandle));
        }
        // 2. Entity Filter (by tag ID)
        else if (selectedEntityId) {
            constraints.push(where('linkedTags', 'array-contains', selectedEntityId));
        }
        // 3. Tag Filter (by slug)
        else if (tagSlug) {
            constraints.push(where('linkedTags', 'array-contains', tagSlug));
        }
        // 4. Explicit Handles List
        else if (handles && handles.length > 0) {
            const searchHandles = handles.slice(0, 30); // Firestore 'in' supports up to 30 values
            constraints.push(where('handleId', 'in', searchHandles));
        }
        // 5. All Tweets (no filter)
        
        // Add ordering
        constraints.push(orderBy(orderField, 'desc'));
        
        // Add cursor for pagination
        if (isLoadMore && lastDoc) {
            constraints.push(startAfter(lastDoc));
        }
        
        // Add limit
        constraints.push(limit(fetchSize));
        
        return query(tweetsRef, ...constraints);
    }, [selectedHandle, selectedEntityId, tagSlug, handles, sortOption, lastDoc]);

    // Fetch tweets (initial or load more)
    const fetchTweets = useCallback(async (isLoadMore: boolean = false) => {
        if (!db) return;
        const firestore = db; // non-null alias for use inside closures (TS narrowing)
        
        // Safety: Don't fetch if we've hit the max
        if (totalFetched >= MAX_TOTAL_TWEETS && isLoadMore) {
            setHasMore(false);
            return;
        }
        
        if (isLoadMore) {
            setIsLoadingMore(true);
        } else {
            setIsLoading(true);
            setError('');
        }

        try {
            let fetchedTweets: Tweet[];
            const fetchSize = isWidgetMode
                ? maxTweets
                : (isLoadMore ? LOAD_MORE_SIZE : INITIAL_FETCH_SIZE);

            if (useChunkedFetch) {
                // ── Scatter-gather path (>30 handles) ─────────────────────────────────
                // Firestore caps `in` at 30 values, so we split handles into chunks of 30,
                // run one ordered+limited query per chunk in PARALLEL, then merge with the
                // pure helper. Each handleId lives in exactly one chunk, so chunk results
                // are disjoint (no dedupe). The load-more cursor is a VALUE cursor (the
                // orderField value of the last displayed tweet) because a docSnapshot
                // cursor only works for the single chunk it came from.
                const orderField = sortOption === 'engaging' ? 'trendingScore' : 'createdAt';
                const chunks = chunkArray(handles!, FIRESTORE_IN_QUERY_LIMIT);

                // For createdAt the field is a Firestore Timestamp; convert the epoch-ms
                // boundary back into a Timestamp so startAfter compares correctly.
                let cursor: any = boundaryValue;
                if (isLoadMore && orderField === 'createdAt' && typeof boundaryValue === 'number') {
                    cursor = Timestamp.fromMillis(boundaryValue);
                }

                const snapshots = await Promise.all(chunks.map(chunk => {
                    const constraints: any[] = [
                        where('isHidden', '==', false),
                        where('handleId', 'in', chunk),
                        orderBy(orderField, 'desc'),
                    ];
                    if (isLoadMore && cursor != null) {
                        constraints.push(startAfter(cursor));
                    }
                    constraints.push(limit(fetchSize));
                    return getDocs(query(collection(firestore, 'tweets'), ...constraints));
                }));

                const chunkTweets: Tweet[][] = snapshots.map(snap =>
                    snap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Tweet[]
                );
                // A chunk that returned a full page may have more docs past its limit.
                const anyChunkFull = snapshots.some(snap => snap.docs.length === fetchSize);

                const merged = mergeChunkedTweets<Tweet>(chunkTweets, orderField, fetchSize, { anyChunkFull });
                fetchedTweets = merged.tweets;

                if (fetchedTweets.length > 0) {
                    setBoundaryValue(merged.boundaryValue);
                }
                setHasMore(merged.hasMore);
            } else {
                // ── Single-query path (unchanged for <=30 handles & all other filters) ──
                const q = buildQuery(isLoadMore);
                if (!q) return;

                const snapshot = await getDocs(q);
                fetchedTweets = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as Tweet[];

                // Update pagination cursor
                if (snapshot.docs.length > 0) {
                    setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
                }

                // Check if there are more tweets
                setHasMore(snapshot.docs.length === fetchSize);
            }

            // Update tweets - append for load more, replace for initial
            if (isLoadMore) {
                setTweets(prev => [...prev, ...fetchedTweets]);
                setTotalFetched(prev => prev + fetchedTweets.length);
            } else {
                setTweets(fetchedTweets);
                setTotalFetched(fetchedTweets.length);
            }

            setLastFetchedAt(new Date());

            // Fetch profiles for new tweets
            if (fetchedTweets.length > 0) {
                await fetchProfiles(fetchedTweets, isLoadMore);
            }

            console.log(`🐦 [TwitterFeed] ${isLoadMore ? 'Loaded more' : 'Initial load'}: ${fetchedTweets.length} tweets (total: ${isLoadMore ? totalFetched + fetchedTweets.length : fetchedTweets.length})`);

        } catch (err: any) {
            console.error('Error fetching tweets:', err);
            if (!isLoadMore) {
                setError(err.message || 'Failed to load tweets');
            }
        } finally {
            if (isLoadMore) {
                setIsLoadingMore(false);
            } else {
                setIsLoading(false);
            }
        }
    }, [buildQuery, totalFetched, useChunkedFetch, handles, sortOption, boundaryValue, isWidgetMode, maxTweets]);

    // Fetch profile data for tweets
    const fetchProfiles = async (newTweets: Tweet[], isLoadMore: boolean) => {
        try {
            if (!db) return;

            const profileMap: Record<string, string> = {};
            const handleTagData: { handleId: string; linkedTagIds: string[] }[] = [];
            const allHandleIds = Array.from(new Set(newTweets.map(t => t.handleId)));

            // Collect profiles from tweet data as FALLBACK (these may be stale Twitter CDN URLs)
            const tweetProfileFallback: Record<string, string> = {};
            newTweets.forEach(tweet => {
                if (tweet.authorProfilePic) {
                    tweetProfileFallback[tweet.handleId] = tweet.authorProfilePic;
                }
            });

            // Fetch handle documents - these have the FRESH proxied profile images
            const handlesToFetch = allHandleIds.filter(id => !profiles[id]);

            if (handlesToFetch.length > 0 || !isLoadMore) {
                const fetchHandles = isLoadMore ? handlesToFetch : allHandleIds;

                for (let i = 0; i < fetchHandles.length; i += 10) {
                    const batch = fetchHandles.slice(i, i + 10);
                    if (batch.length === 0) continue;

                    const q = query(
                        collection(db, 'twitterHandles'),
                        where(documentId(), 'in', batch)
                    );
                    const snapshot = await getDocs(q);

                    snapshot.docs.forEach(doc => {
                        const data = doc.data();
                        // Prefer handle's profileImage (proxied/fresh) over tweet's authorProfilePic (may be stale)
                        const img = data.profileImage || data.profilePicUrl;
                        if (img) {
                            profileMap[doc.id] = img;
                        }
                        if (data.linkedTags?.length > 0) {
                            handleTagData.push({
                                handleId: doc.id,
                                linkedTagIds: data.linkedTags
                            });
                        }
                    });
                }
            }

            // Use tweet fallback only for handles we couldn't fetch from twitterHandles collection
            allHandleIds.forEach(handleId => {
                if (!profileMap[handleId] && !profiles[handleId] && tweetProfileFallback[handleId]) {
                    profileMap[handleId] = tweetProfileFallback[handleId];
                }
            });

            setProfiles(prev => ({ ...prev, ...profileMap }));

            // Batch fetch linked tags
            if (handleTagData.length > 0) {
                const allTagIds = new Set<string>();
                handleTagData.forEach(h => h.linkedTagIds.forEach(id => allTagIds.add(id)));

                if (allTagIds.size > 0) {
                    const tagMap = await tagService.getTagsBatch(Array.from(allTagIds));
                    const handleToTags = new Map<string, Tag[]>();
                    
                    handleTagData.forEach(({ handleId, linkedTagIds }) => {
                        const tags = linkedTagIds
                            .map(id => tagMap.get(id))
                            .filter((tag): tag is Tag => tag !== undefined && tag.isActive);
                        if (tags.length > 0) {
                            handleToTags.set(handleId, tags);
                        }
                    });

                    setHandleTagMap(prev => new Map([...prev, ...handleToTags]));
                }
            }
        } catch (err) {
            console.error('Error fetching profiles:', err);
        }
    };

    // Fetch handles for filter dropdown
    const fetchHandles = async () => {
        try {
            if (!db) return;
            if (handles && handles.length > 0) {
                setAllHandles(handles);
                return;
            }
            const q = query(collection(db, 'twitterHandles'), where('status', '==', 'active'));
            const snapshot = await getDocs(q);
            setAllHandles(snapshot.docs.map(doc => doc.id));
        } catch (err) {
            console.error('Error fetching handles:', err);
        }
    };

    // Reset and refetch when query params change
    useEffect(() => {
        const currentParams = `${tagSlug}-${handles?.join(',')}-${selectedEntityId}-${selectedHandle}-${sortOption}`;
        
        if (currentParams !== queryParamsRef.current) {
            queryParamsRef.current = currentParams;
            // Reset pagination state
            setLastDoc(null);
            setBoundaryValue(null);
            setHasMore(true);
            setTotalFetched(0);
            setTweets([]);
            // Fetch fresh data
            fetchTweets(false);
        }
    }, [tagSlug, handles?.join(','), selectedEntityId, selectedHandle, sortOption]);

    // Initial fetch
    useEffect(() => {
        fetchHandles();
        if (queryParamsRef.current === '') {
            queryParamsRef.current = `${tagSlug}-${handles?.join(',')}-${selectedEntityId}-${selectedHandle}-${sortOption}`;
            fetchTweets(false);
        }
    }, []);

    // Filter and sort tweets (client-side refinement)
    const displayedTweets = useMemo(() => {
        let filtered = [...tweets];

        // Keep self-threads (the handle replying to ITSELF — popular, high-signal continuations
        // like a "tl;dr 🌺" follow-up) but still drop replies to OTHER accounts (conversation noise).
        const isSelfThread = (t: Tweet) =>
            !!t.isReply && !!t.inReplyToUsername && !!t.username &&
            t.inReplyToUsername.toLowerCase() === t.username.toLowerCase();
        filtered = filtered.filter(t => !t.isReply || isSelfThread(t));
        
        // Filter out hidden tweets (client-side fallback - query should already filter these)
        filtered = filtered.filter(t => !t.isHidden);

        // Additional client-side sorting for 'engaging' mode with time-decay
        if (sortOption === 'engaging') {
            // Time-decay scoring: recent tweets get a boost, old tweets decay
            // This prevents ancient viral tweets from always dominating
            const now = Date.now();
            
            filtered.sort((a, b) => {
                const scoreA = a.trendingScore ?? ((a.metrics.likes || 0) + (a.metrics.retweets || 0) + (a.metrics.replies || 0));
                const scoreB = b.trendingScore ?? ((b.metrics.likes || 0) + (b.metrics.retweets || 0) + (b.metrics.replies || 0));
                
                // Calculate age in days
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date();
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date();
                const ageA = (now - dateA.getTime()) / (1000 * 60 * 60 * 24); // days
                const ageB = (now - dateB.getTime()) / (1000 * 60 * 60 * 24);
                
                // Apply decay: score / (1 + age/30) - tweets lose ~50% weight after 30 days
                const decayedScoreA = scoreA / (1 + ageA / 30);
                const decayedScoreB = scoreB / (1 + ageB / 30);
                
                return decayedScoreB - decayedScoreA;
            });

            // Diversity filter for large feeds
            if (!handles || handles.length > 5) {
                const accountCounts = new Map<string, number>();
                const maxPerAccount = 3; // Slightly increased for better content
                filtered = filtered.filter(tweet => {
                    const count = accountCounts.get(tweet.handleId) || 0;
                    if (count < maxPerAccount) {
                        accountCounts.set(tweet.handleId, count + 1);
                        return true;
                    }
                    return false;
                });
            }
        }

        return filtered;
    }, [tweets, sortOption, handles]);

    const shouldHideEntityTags = hideEntityTags || selectedHandle !== 'all';

    // Handle "Load More" click - fetches from Firestore
    const handleLoadMore = () => {
        if (!isLoadingMore && hasMore) {
            fetchTweets(true);
        }
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return '';
        let date: Date;
        if (timestamp instanceof Date) {
            date = timestamp;
        } else if (timestamp.toDate) {
            date = timestamp.toDate();
        } else if (timestamp.seconds) {
            date = new Date(timestamp.seconds * 1000);
        } else {
            date = new Date(timestamp);
        }
        
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    const formatNumber = (num: number) => {
        if (!num) return '0';
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return num.toString();
    };

    // Loading skeleton
    if (isLoading) {
        return (
            <div className={`space-y-4 ${className}`}>
                {[...Array(variant === 'compact' ? 3 : 4)].map((_, i) => (
                    <div key={i} className="bg-white dark:bg-brand-dark-lighter rounded-lg p-4 border border-gray-200 dark:border-neutral-800 animate-pulse">
                        <div className="flex items-start space-x-3">
                            <div className="w-10 h-10 bg-gray-200 dark:bg-slate-700 rounded-full" />
                            <div className="flex-1 space-y-2">
                                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/4" />
                                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div className={`bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 ${className}`}>
                <p className="text-red-800 dark:text-red-200 text-sm">Error loading tweets: {error}</p>
            </div>
        );
    }

    if (tweets.length === 0) {
        return (
            <div className={`bg-gray-50 dark:bg-brand-dark-lighter border border-gray-200 dark:border-neutral-800 rounded-lg p-8 text-center flex flex-col items-center ${className}`}>
                <StateMark size={72} className="mb-4 opacity-80" />
                <p className="text-gray-500 dark:text-gray-400 text-sm">No tweets found</p>
            </div>
        );
    }

    return (
        <div className={`space-y-3 ${className}`}>
            {/* Controls Bar */}
            {showControls && (
                <div className="flex items-center justify-between gap-4 px-1 pb-3 border-b border-gray-100 dark:border-neutral-800">
                    <div className="min-w-0 flex-1">
                        {typeof title === 'string' ? (
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                                {title}
                            </h3>
                        ) : (
                            title || <h3 className="text-lg font-bold text-gray-900 dark:text-white">All Tweets</h3>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Handle Filter Dropdown - only show if multiple handles */}
                        {availableHandles && availableHandles.length > 1 && onHandleChange && (
                            <div className="relative">
                                <select
                                    value={externalSelectedHandle || 'all'}
                                    onChange={(e) => onHandleChange(e.target.value)}
                                    className="appearance-none bg-white dark:bg-neutral-800 text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-neutral-700 rounded-lg px-3 py-1.5 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 cursor-pointer hover:border-gray-300 dark:hover:border-neutral-600 transition-colors"
                                >
                                    <option value="all">All ({availableHandles.length})</option>
                                    {availableHandles.map(handle => (
                                        <option key={handle} value={handle}>@{handle}</option>
                                    ))}
                                </select>
                                <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" />
                            </div>
                        )}
                        {/* Sort Dropdown */}
                        <div className="relative">
                            <select
                                value={sortOption}
                                onChange={(e) => handleSortChange(e.target.value as SortOption)}
                                className="appearance-none bg-white dark:bg-neutral-800 text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-neutral-700 rounded-lg px-3 py-1.5 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 cursor-pointer hover:border-gray-300 dark:hover:border-neutral-600 transition-colors"
                            >
                                <option value="latest">Latest</option>
                                <option value="engaging">Top Rated</option>
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" />
                        </div>
                    </div>
                </div>
            )}

            {/* Tweet List */}
            <div className="space-y-3">
                {displayedTweets.length > 0 ? displayedTweets.map((tweet) => (
                    variant === 'compact' ? (
                        <CompactTweetCard
                            key={tweet.id}
                            tweet={tweet}
                            profileImage={profiles[tweet.handleId] || tweet.authorProfilePic}
                            linkedTags={handleTagMap.get(tweet.handleId)}
                            hideEntityTags={shouldHideEntityTags}
                            entitySocialBase={entitySocialBase}
                            onEntityClick={(entityId, entitySlug) => {
                                // Focus-in-place when the host opts in (chain dashboard);
                                // otherwise navigate to the (scoped) social entity view.
                                if (onEntitySelect) onEntitySelect(entityId, entitySlug);
                                else router.push(`${entitySocialBase}?entity=${entitySlug}`);
                            }}
                        />
                    ) : (
                        <div
                            key={tweet.id}
                            className="bg-white dark:bg-brand-dark-lighter rounded-lg p-4 border border-gray-200 dark:border-neutral-800 hover:border-scout-gold dark:hover:border-scout-gold transition-colors"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center space-x-2">
                                    {profiles[tweet.handleId] || tweet.authorProfilePic ? (
                                        <div className="relative w-10 h-10">
                                            <Image
                                                src={profiles[tweet.handleId] || tweet.authorProfilePic || ''}
                                                alt={tweet.username}
                                                fill
                                                className="rounded-full object-cover"
                                            />
                                        </div>
                                    ) : (
                                        <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                                            {tweet.username.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div>
                                        <p className="font-semibold text-gray-900 dark:text-white">
                                            {tweet.username}
                                        </p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            @{tweet.handleId}
                                        </p>
                                    </div>
                                </div>
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                    {formatDate(tweet.createdAt)}
                                </span>
                            </div>
                            <p className="text-gray-900 dark:text-gray-100 mb-3 whitespace-pre-wrap">
                                {tweet.text}
                            </p>
                            {tweet.media && tweet.media.length > 0 && (
                                <div className="mb-3 rounded-lg overflow-hidden">
                                    {tweet.media[0].type === 'photo' && (
                                        <div className="relative w-full h-64 bg-gray-100 dark:bg-slate-700">
                                            <Image
                                                src={tweet.media[0].url}
                                                alt="Tweet media"
                                                fill
                                                className="object-cover"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="flex items-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
                                <span className="flex items-center space-x-1 hover:text-blue-500 transition-colors">
                                    <MessageCircle className="w-4 h-4" />
                                    <span>{formatNumber(tweet.metrics.replies)}</span>
                                </span>
                                <span className="flex items-center space-x-1 hover:text-green-500 transition-colors">
                                    <Repeat2 className="w-4 h-4" />
                                    <span>{formatNumber(tweet.metrics.retweets)}</span>
                                </span>
                                <span className="flex items-center space-x-1 hover:text-red-500 transition-colors">
                                    <Heart className="w-4 h-4" />
                                    <span>{formatNumber(tweet.metrics.likes)}</span>
                                </span>
                            </div>
                        </div>
                    )
                )) : (
                    <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-neutral-800/50 rounded-lg border border-dashed border-gray-200 dark:border-neutral-700">
                        No tweets found matching filters
                    </div>
                )}
            </div>

            {/* Load More Button - Hidden in widget mode, shows for full feeds */}
            {!isWidgetMode && hasMore && totalFetched < MAX_TOTAL_TWEETS && (
                <button
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    className="w-full py-2.5 text-xs text-gray-500 dark:text-gray-400 font-medium hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-neutral-800 rounded-lg transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isLoadingMore ? (
                        <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Loading...
                        </>
                    ) : (
                        <>
                            Load More
                            <ChevronDown className="w-3 h-3" />
                        </>
                    )}
                </button>
            )}
            
            {/* End of feed indicator */}
            {/* End of feed indicator - hidden in widget mode */}
            {!isWidgetMode && !hasMore && tweets.length > INITIAL_FETCH_SIZE && (
                <div className="text-center py-2">
                    <p className="text-[10px] text-neutral-500">
                        Showing all {displayedTweets.length} tweets
                    </p>
                </div>
            )}

            {/* Last Updated Notice - hidden in widget mode */}
            {!isWidgetMode && lastFetchedAt && (
                <div className="text-center pt-2 border-t border-neutral-800/30">
                    <p className="text-[10px] text-neutral-500">
                        Feed loaded {formatDate(lastFetchedAt)} • Updates every few hours
                    </p>
                </div>
            )}
        </div>
    );
}
